/**
 * HalloweenTheme — subtle atmospheric overlay that layers on top of
 * the app when the user's active theme is "halloween". Added
 * 2026-08-06 per user ask: "we just transitioned to the halloween
 * theme and feel like we can make it more".
 *
 * Design brief (user chose intensity 2a — subtle):
 *   • Cobwebs in top-left + top-right corners (SVG, low-opacity)
 *   • A soft full-moon glow behind the header
 *   • One bat silhouette crossing the screen every ~20s
 *   • Subtle fog band at the very bottom
 *   • One-time soft chime on activation, ALWAYS mutable (respects
 *     the same `wow:audio:muted` localStorage flag SpiderManTheme
 *     uses, so a single mute toggle in Settings kills every theme's
 *     sound at once)
 *
 * Non-goals:
 *   • No jump-scares, no glowing eyes in corners, no thunder — the
 *     app is a work tool and managers open it during shifts.
 *   • No block on interactivity — all overlays are pointer-events:none.
 *
 * Bundle discipline: SVGs are inline strings, no image assets, no
 * external icon library beyond phosphor which is already vendored.
 */
import { useEffect, useState, useRef } from "react";
import { useTheme } from "../ThemeContext";

// One-shot spooky chime — a minor-key harp arpeggio (Web Audio API,
// no external file). Kept tiny (~450ms) so it never overstays.
const playChime = () => {
  try {
    // Two mute paths, both respected:
    //   1. Global cue mute (`wow:audio:muted`) — flipped when the user
    //      silences all in-app sound.
    //   2. Halloween-only mute (`wow:audio:halloween:muted`) — flipped
    //      from the Settings toggle so the theme's chime can be
    //      silenced without muting notifications.
    if (localStorage.getItem("wow:audio:muted") === "1") return;
    if (localStorage.getItem("wow:audio:halloween:muted") === "1") return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    // A minor arpeggio: A4 → C5 → E5 → A5
    const notes = [440, 523.25, 659.25, 880];
    notes.forEach((freq, i) => {
      const t = ctx.currentTime + i * 0.11;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.06, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1200);
  } catch { /* audio unsupported */ }
};

// Inline SVG cobweb — solid white lines, very low alpha, so it works
// on any background. Two mirrored copies for top-left / top-right.
const Cobweb = ({ side = "left" }) => (
  <svg
    width="140" height="140" viewBox="0 0 140 140"
    className={`absolute top-0 ${side === "left" ? "left-0" : "right-0"} pointer-events-none select-none`}
    style={{ opacity: 0.14, transform: side === "right" ? "scaleX(-1)" : undefined }}
    aria-hidden="true"
  >
    <g stroke="#FDE68A" strokeWidth="0.75" fill="none" strokeLinecap="round">
      {/* Radial spokes from the top-left anchor */}
      <line x1="0" y1="0" x2="140" y2="20" />
      <line x1="0" y1="0" x2="120" y2="50" />
      <line x1="0" y1="0" x2="90" y2="90" />
      <line x1="0" y1="0" x2="50" y2="120" />
      <line x1="0" y1="0" x2="20" y2="140" />
      {/* Concentric arcs */}
      <path d="M 32 0 Q 25 25 0 32" />
      <path d="M 58 0 Q 44 44 0 58" />
      <path d="M 90 0 Q 68 68 0 90" />
      <path d="M 120 0 Q 88 88 0 120" />
    </g>
  </svg>
);

// Soft moon halo — sits behind the header. Not a hard circle; a
// blurred radial gradient so it feels like ambient moonlight rather
// than a sticker.
const MoonGlow = () => (
  <div
    className="fixed top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[520px] h-[520px] pointer-events-none z-0"
    style={{
      background: "radial-gradient(circle at center, rgba(253, 230, 138, 0.16) 0%, rgba(253, 230, 138, 0.06) 35%, transparent 65%)",
      filter: "blur(6px)",
    }}
    aria-hidden="true"
  />
);

// Bottom fog band — a semi-transparent gradient anchored to the
// bottom that fades upward. Extremely subtle.
const BottomFog = () => (
  <div
    className="fixed bottom-0 left-0 right-0 h-24 pointer-events-none z-0"
    style={{
      background: "linear-gradient(to top, rgba(124, 45, 18, 0.28) 0%, rgba(124, 45, 18, 0.10) 40%, transparent 100%)",
    }}
    aria-hidden="true"
  />
);

// Bat silhouette — CSS-only, uses an inline SVG data URI so no image
// fetch. Slides across the screen once every 20s, alternating heights
// so it doesn't feel scripted.
const BatFlight = ({ trigger }) => {
  const heightPct = 15 + (trigger % 4) * 12; // 15%, 27%, 39%, 51%
  const key = `bat-${trigger}`;
  if (trigger === 0) return null;
  return (
    <div
      key={key}
      className="fixed left-0 pointer-events-none z-0"
      style={{
        top: `${heightPct}%`,
        animation: "bat-cross 8s linear forwards",
      }}
      aria-hidden="true"
    >
      <svg width="42" height="26" viewBox="0 0 42 26" style={{ opacity: 0.55 }}>
        {/* Simplified bat silhouette — two wing arcs + body */}
        <path
          fill="#1a0a1e"
          d="M21 13 L15 8 L10 11 L6 6 L4 12 L8 15 L4 17 L10 18 L15 15 L21 20 L27 15 L32 18 L38 17 L34 15 L38 12 L36 6 L32 11 L27 8 Z"
        />
      </svg>
    </div>
  );
};

/**
 * HalloweenTheme — mounted once at App.js top-level. Only renders
 * anything when `active === true`.
 */
export default function HalloweenTheme({ active }) {
  const [batTrigger, setBatTrigger] = useState(0);
  const playedChimeThisSession = useRef(false);

  // One-shot chime on the first activation of the session.
  useEffect(() => {
    if (!active || playedChimeThisSession.current) return;
    playedChimeThisSession.current = true;
    // Small delay so the theme's ambient decorations render before the
    // audio fires — feels like the room lights dim first, then the
    // sound arrives.
    const t = setTimeout(() => playChime(), 350);
    return () => clearTimeout(t);
  }, [active]);

  // Occasional bat flight — every 20s while the theme is active. Skip
  // when the document is hidden (tab in background) so we don't burn
  // battery animating for nobody. Uses the standard Page Visibility
  // API pattern that the rest of the app uses via `useVisibilityInterval`.
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") {
        setBatTrigger((n) => n + 1);
      }
    };
    // First bat at 6s (welcome), then every 20s.
    const first = setTimeout(tick, 6000);
    const interval = setInterval(tick, 20000);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [active]);

  if (!active) return null;

  return (
    <>
      {/* Keyframes injected inline so we don't need to touch the
          global stylesheet for a theme-scoped animation. */}
      <style>{`
        @keyframes bat-cross {
          0%   { transform: translateX(-60px) translateY(0)   scale(0.9); }
          25%  { transform: translateX(25vw)  translateY(-8px) scale(1.0); }
          50%  { transform: translateX(50vw)  translateY(4px)  scale(0.95); }
          75%  { transform: translateX(75vw)  translateY(-6px) scale(1.05); }
          100% { transform: translateX(calc(100vw + 60px)) translateY(0) scale(0.9); }
        }
        @keyframes cobweb-sway {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-1.2deg); }
        }
      `}</style>
      <div className="fixed inset-0 pointer-events-none z-0" data-testid="halloween-theme-overlay">
        <MoonGlow />
        <div style={{ animation: "cobweb-sway 12s ease-in-out infinite" }}>
          <Cobweb side="left" />
        </div>
        <div style={{ animation: "cobweb-sway 14s ease-in-out infinite" }}>
          <Cobweb side="right" />
        </div>
        <BatFlight trigger={batTrigger} />
        <BottomFog />
      </div>
    </>
  );
}

// Small hook consumers can import to check "is the halloween theme on?"
// without re-reading the ThemeContext everywhere. Not currently used
// but exposed so a page can conditionally render pumpkin emojis, etc.
export function useIsHalloween() {
  const { theme } = useTheme();
  return (theme?.key || theme?.id || "") === "halloween";
}
